import React, { useEffect, useRef, useState } from "react";
import "./EmpackPresentation.css";

const EmpackPresentation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section className="empack-section" ref={sectionRef}>
      <div className="empack-container">
        {/* Header */}
        <div className={`empack-header ${isVisible ? "animate" : ""}`}>
          <img className="empack-logo" src="/images/logo.svg" alt="EMPACK Logo" />
          <p className="empack-subtitle">
            Fondée en 2012 | Tunisie | Solutions d'emballage personnalisées
          </p>
        </div>

        {/* Introduction */}
        <div className={`empack-content ${isVisible ? "animate-delay-1" : ""}`}>
          <h2>Qui sommes-nous ?</h2>
          <p>
            EMPACK est une entreprise tunisienne fondée en 2012, spécialisée dans la
            fabrication de boîtes en carton et de sacs imprimés personnalisés. Depuis sa
            création, EMPACK s'est imposée comme un acteur clé dans le secteur de
            l'emballage en Tunisie, en proposant des solutions sur mesure et de qualité
            pour divers secteurs industriels.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className={`empack-content ${isVisible ? "animate-delay-2" : ""}`}>
          <h2>Mission & Vision</h2>
          <div className="mission-vision">
            <div className="mission">
              <h3>Mission</h3>
              <p>
                L'objectif d'EMPACK est de fournir des solutions d'emballage innovantes,
                durables et personnalisées aux entreprises, afin de répondre à leurs
                besoins spécifiques tout en respectant des standards de qualité élevés.
              </p>
            </div>
            <div className="vision">
              <h3>Vision</h3>
              <p>
                Devenir un leader régional dans le secteur de l’emballage, en contribuant
                à la durabilité de l'environnement et à la satisfaction de nos clients
                grâce à des produits d'emballage qui allient performance et esthétisme.
              </p>
            </div>
          </div>
        </div>

        {/* Products & Services */}
        <div className={`empack-content ${isVisible ? "animate-delay-3" : ""}`}>
          <h2>Produits & Services</h2>
          <div className="products-grid">
            <div className="product-card">
              <h3>Boîtes en carton personnalisées</h3>
              <p>
                Fabriquées sur mesure, ces boîtes sont adaptées à une grande variété de
                produits, allant des biens de consommation aux produits industriels. Elles
                sont conçues pour offrir une protection optimale.
              </p>
            </div>
            <div className="product-card">
              <h3>Sacs imprimés personnalisés</h3>
              <p>
                En papier ou carton, avec impressions personnalisées pour le transport de
                marchandises et des besoins promotionnels, fabriqués avec des matériaux
                écologiques.
              </p>
            </div>
            <div className="product-card">
              <h3>Emballages écologiques</h3>
              <p>
                Fabriqués en carton recyclé ou recyclable pour réduire l’empreinte
                écologique des entreprises.
              </p>
            </div>
            <div className="product-card">
              <h3>Solutions sur mesure</h3>
              <p>
                Conception adaptée avec conseil en design, création de prototypes et
                tests pour répondre aux besoins spécifiques.
              </p>
            </div>
          </div>
        </div>

        {/* Technology & Innovation */}
        <div className={`empack-content ${isVisible ? "animate-delay-4" : ""}`}>
          <h2>Technologie & Innovation</h2>
          <p>
            EMPACK investit constamment dans des technologies de production modernes
            pour garantir la qualité et l'efficacité de ses produits. L'entreprise utilise
            des machines de pointe pour l'impression, la découpe et l'assemblage des
            boîtes et sacs, permettant de répondre rapidement aux commandes, même pour
            des séries limitées ou des projets personnalisés.
          </p>
        </div>

        {/* Engagement envers la qualité */}
        <div className={`empack-content ${isVisible ? "animate-delay-5" : ""}`}>
          <h2>Engagement envers la qualité</h2>
          <p>
            La qualité est au cœur de la stratégie d'EMPACK. L'entreprise s'engage à
            respecter les normes les plus strictes à chaque étape de la production.
            Grâce à un contrôle qualité rigoureux, chaque produit est soumis à des tests
            pour assurer sa conformité avec les exigences des clients.
          </p>
        </div>

        {/* Responsabilité sociale et environnementale */}
        <div className={`empack-content ${isVisible ? "animate-delay-6" : ""}`}>
          <h2>Responsabilité sociale et environnementale</h2>
          <p>
            EMPACK met un point d'honneur à adopter des pratiques durables et
            responsables. L'entreprise utilise des matériaux recyclables et s'efforce
            de réduire son empreinte carbone tout au long de son processus de
            fabrication. De plus, EMPACK participe activement à des initiatives locales
            pour promouvoir la durabilité et le respect de l'environnement.
          </p>
        </div>

        {/* Marché et clients */}
        <div className={`empack-content ${isVisible ? "animate-delay-7" : ""}`}>
          <h2>Marché et clients</h2>
          <p>
            EMPACK dessert une large gamme de secteurs d'activité, incluant :
          </p>
          <ul className="market-list">
            <li>Industrie alimentaire</li>
            <li>Cosmétiques et produits de soin</li>
            <li>Électronique</li>
            <li>Vêtements et accessoires</li>
            <li>Distribution de produits divers</li>
          </ul>
          <p>
            Les clients d'EMPACK sont principalement des entreprises tunisiennes, mais
            l'entreprise est en pleine expansion et aspire à se développer à
            l'international.
          </p>
        </div>

        {/* Why Choose EMPACK */}
        <div className={`empack-content ${isVisible ? "animate-delay-8" : ""}`}>
          <h2>
            Pourquoi choisir EMPACK <span><img className="empack-h2-icon" src="/images/icon.svg" alt="Icon" /></span> ?
          </h2>
          <div className="why-choose-blocks">
            <div className="why-choose-item">
              <span className="tick">✔</span>
              <div className="text">
                <span>Qualité supérieure :</span>
                <p>Des produits robustes et durables adaptés à vos besoins spécifiques.</p>
              </div>
            </div>
            <div className="why-choose-item">
              <span className="tick">✔</span>
              <div className="text">
                <span>Personnalisation :</span>
                <p>Des solutions sur mesure pour un impact visuel fort et une différenciation sur le marché.</p>
              </div>
            </div>
            <div className="why-choose-item">
              <span className="tick">✔</span>
              <div className="text">
                <span>Responsabilité environnementale :</span>
                <p>Un engagement envers la fabrication d’emballages écologiques.</p>
              </div>
            </div>
            <div className="why-choose-item">
              <span className="tick">✔</span>
              <div className="text">
                <span>Innovation :</span>
                <p>Des technologies de production avancées garantissant qualité et efficacité.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Conclusion */}
        <div className={`empack-footer ${isVisible ? "animate-delay-9" : ""}`}>
          <h2>Conclusion</h2>
          <p>
            Depuis sa création en 2012, EMPACK a su se diversifier et se perfectionner
            dans la fabrication d’emballages et de sacs personnalisés en Tunisie. Grâce
            à son expertise, son engagement en matière de qualité, de durabilité et
            d'innovation, EMPACK continue de répondre aux attentes croissantes de ses
            clients, tout en contribuant au développement du secteur de l’emballage en
            Tunisie et au-delà.
          </p>
        </div>
      </div>
      <div className="background-accent" />
    </section>
  );
};

export default EmpackPresentation;